// Change a plan for sales from now on (membershipTypes.update): its name, price, the visits it
// includes and their job type, the member discount, and whether it is still sold. Members keep the
// terms they bought; only what changed is sent, with the version it was read at.

import { useState } from 'react';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import { CANCEL_REFUND_WORDS, type CancelRefund } from './model.ts';
import { planForm, planProblems, planSavedText, planUpdate, type EditablePlan } from './plan-edit-model.ts';

export function EditPlan({
  plan,
  onClose,
  onSaved,
}: {
  plan: EditablePlan;
  onClose: () => void;
  onSaved: (said: string) => void;
}) {
  const types = useQuery<{ jobTypes: { id: string; name: string }[] }>('jobTypes.list', {});
  const [f, setF] = useState(() => planForm(plan));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems = planProblems(f);
  const update = planUpdate(plan, f);
  const save = async () => {
    setTried(true);
    if (problems.length || !update) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('membershipTypes.update', update);
      onSaved(planSavedText(f.name.trim(), plan.activeMembers));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid"
      aria-label={`Change ${plan.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="muted small wide">
        For sales from now on.{' '}
        {plan.activeMembers
          ? `${plan.activeMembers} ${plan.activeMembers === 1 ? 'member keeps' : 'members keep'} the terms they bought.`
          : ''}{' '}
        A new price is its price book item’s, kept in the price history.
      </p>
      <label className="wide">
        <span className="flabel">Plan name</span>
        <input
          className="field"
          maxLength={100}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Price a term</span>
        <input
          className="field"
          inputMode="decimal"
          value={f.price}
          onChange={(e) => setF({ ...f, price: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Term (months)</span>
        <input
          className="field"
          inputMode="numeric"
          value={f.term}
          onChange={(e) => setF({ ...f, term: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Visits included</span>
        <input
          className="field"
          inputMode="numeric"
          value={f.visits}
          onChange={(e) => setF({ ...f, visits: e.target.value })}
        />
      </label>
      {Number(f.visits) > 0 && (
        <label>
          <span className="flabel">Visit type</span>
          <select
            className="field"
            value={f.jobType}
            onChange={(e) => setF({ ...f, jobType: e.target.value })}
          >
            <option value="">Pick a job type…</option>
            {(types.data?.jobTypes ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        <span className="flabel">Member discount (%)</span>
        <input
          className="field"
          inputMode="decimal"
          value={f.discount}
          onChange={(e) => setF({ ...f, discount: e.target.value })}
        />
      </label>
      <label className="wide">
        <span className="flabel">At a cancel</span>
        <select
          className="field"
          value={f.cancelRefund}
          onChange={(e) => setF({ ...f, cancelRefund: e.target.value as CancelRefund })}
        >
          {(Object.keys(CANCEL_REFUND_WORDS) as CancelRefund[]).map((k) => (
            <option key={k} value={k}>
              {CANCEL_REFUND_WORDS[k]}
            </option>
          ))}
        </select>
        <span className="muted small block">
          What a member paid and hasn’t used yet, when their membership is cancelled. This is the company’s
          policy: it applies to every member of the plan who cancels, not only new sales.
        </span>
      </label>
      <label className="checkrow wide">
        <input
          type="checkbox"
          checked={f.active}
          onChange={(e) => setF({ ...f, active: e.target.checked })}
        />{' '}
        Still sold (unticked, nobody new can buy it; members keep it)
      </label>
      {tried && problems.length > 0 && (
        <ul className="error small wide" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {tried && !problems.length && !update && <p className="muted small wide">Nothing changed yet.</p>}
      <div className="row-actions wide">
        <button type="button" className="btn soft" onClick={onClose}>
          Cancel
        </button>
        <SaveButton busy={busy} busyLabel="Saving…" error={error} onClick={() => void save()}>
          Save the plan
        </SaveButton>
      </div>
    </form>
  );
}
